import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useSelection } from "../../context/SelectionContext";
import { usePermissions } from "../../hooks/usePermissions";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useApiMutation } from "../../hooks/useApiMutation";
import { notificationsApi } from "../../api/endpoints/notifications.api";
import type { Notification } from "../../api/types";

function useTimeAgo() {
  const { t } = useTranslation();
  return useCallback(
    (dateStr: string): string => {
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return t("common:time.justNow");
      if (mins < 60) return t("common:time.minutesAgo", { count: mins });
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return t("common:time.hoursAgo", { count: hrs });
      return t("common:time.daysAgo", { count: Math.floor(hrs / 24) });
    },
    [t],
  );
}

const NOTIFICATION_ICONS: Record<string, string> = {
  REVIEW_CREATED: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
  REVIEW_RESOLVED: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  DISCREPANCY: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  TASK_REQUIRES_ATTENTION: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
};

export function NotificationBell() {
  const { t } = useTranslation();
  const timeAgo = useTimeAgo();
  const { operationId } = useSelection();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const opId = operationId ?? 0;
  const canView = can("view_notification");
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { data: notifications, refetch } = useApiQuery(
    () => opId > 0 && canView ? notificationsApi.getByOperation(opId) : Promise.resolve([]),
    [opId, canView],
  );

  // Poll every 30s
  useEffect(() => {
    if (opId <= 0) return;
    const interval = setInterval(refetch, 30000);
    return () => clearInterval(interval);
  }, [opId, refetch]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const { mutate: markRead } = useApiMutation((id: number) => notificationsApi.markAsRead(id));
  const { mutate: markAllRead } = useApiMutation(() => notificationsApi.markAllAsRead(opId));

  const all = notifications ?? [];
  // API may return isRead as 1/0 — normalize to boolean
  for (const n of all) n.isRead = !!n.isRead;
  const list = all.filter(n => !n.isRead);
  const unreadCount = list.length;

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllRead(undefined as never);
    } catch { /* silent */ } finally {
      refetch();
    }
  }, [markAllRead, refetch]);

  const handleMarkRead = useCallback(async (n: Notification) => {
    if (n.isRead) return;
    n.isRead = true;
    try {
      await markRead(n.id);
    } catch { /* silent */ }
    refetch();
  }, [markRead, refetch]);

  if (opId <= 0 || !canView) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="relative rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 transition-colors"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
            <span className="text-sm font-semibold text-gray-700">{t("common:topbar.notifications.title")}</span>
            {unreadCount > 0 && (
              <button type="button" onClick={handleMarkAllRead} className="text-xs font-medium text-brand hover:text-brand-dark">
                {t("common:topbar.notifications.markAllRead")}
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {list.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">{t("common:topbar.notifications.empty")}</div>
            ) : (
              list.map(n => (
                <div
                  key={n.id}
                  className={`group flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 ${!n.isRead ? "bg-blue-50/50" : ""}`}
                  onClick={() => {
                    handleMarkRead(n);
                    setOpen(false);
                    navigate(`/notifications/${n.id}`);
                  }}
                >
                  <svg className={`mt-0.5 h-4 w-4 flex-shrink-0 ${!n.isRead ? "text-brand" : "text-gray-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={NOTIFICATION_ICONS[n.type] ?? NOTIFICATION_ICONS.TASK_REQUIRES_ATTENTION} />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm ${!n.isRead ? "font-medium text-gray-900" : "text-gray-600"}`}>{n.message}</p>
                    <p className="mt-0.5 text-[10px] text-gray-400">{timeAgo(n.createdAt)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleMarkRead(n); }}
                    className="mt-0.5 flex-shrink-0 rounded p-0.5 text-gray-300 opacity-0 transition-opacity hover:text-gray-500 group-hover:opacity-100"
                    title={t("common:topbar.notifications.dismiss")}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block border-t border-gray-100 px-4 py-2.5 text-center text-xs font-medium text-brand hover:bg-gray-50 hover:text-brand-dark"
          >
            {t("common:topbar.notifications.viewAll")}
          </Link>
        </div>
      )}
    </div>
  );
}
